import React, { useEffect, useMemo, useState } from 'react';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DateRangeFilter from '../inputs/DateRangeFilter';
import {
  Bar,
  BarChart,
  Cell,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { useAuth } from "../../context/auth";
import { useMessage } from "../../context/message";

const useStyles = makeStyles(() => ({
  root: {
    height: 500,
    padding: 16,
    overflow: 'hidden'
  },
  filter: {
    marginBottom: 8
  },
  seriesFilters: {
    marginBottom: 8
  },
  title: {
    display: 'block',
    marginBottom: 12,
    textAlign: 'center'
  },
  chartArea: {
    width: '100%',
    height: 'calc(100% - 120px)'
  }
}));

const EIA_COLOR = '#03a86b';
const ALPHA_COLOR = '#fc6c05';
const ALPHA_LATEST_COLOR = '#ffd166';

export const defaultInventoryChangeData = [
  { weekEnding: '2024-01-05', alphaBblFriday: -247, eia: -506 },
  { weekEnding: '2024-01-12', alphaBblFriday: -1646, eia: -2099 },
  { weekEnding: '2024-01-19', alphaBblFriday: -2894, eia: -2008 },
  { weekEnding: '2024-01-26', alphaBblFriday: -2289, eia: -1972 },
  { weekEnding: '2024-02-02', alphaBblFriday: -147, eia: -33 },
  { weekEnding: '2024-02-09', alphaBblFriday: 1002, eia: 710 },
  { weekEnding: '2024-02-16', alphaBblFriday: 1151, eia: 741 },
  { weekEnding: '2024-02-23', alphaBblFriday: 1044, eia: 1458 },
  { weekEnding: '2024-03-01', alphaBblFriday: 967, eia: 701 },
  { weekEnding: '2024-03-08', alphaBblFriday: -102, eia: -220 },
  { weekEnding: '2024-03-15', alphaBblFriday: 270, eia: -18 },
  { weekEnding: '2024-03-22', alphaBblFriday: 1690, eia: 2107 },
  { weekEnding: '2024-03-29', alphaBblFriday: -606, eia: -377 },
  { weekEnding: '2024-04-05', alphaBblFriday: 496, eia: -170 },
  { weekEnding: '2024-04-12', alphaBblFriday: 94, eia: 33 },
  { weekEnding: '2024-04-19', alphaBblFriday: -840, eia: -659 },
  { weekEnding: '2024-04-26', alphaBblFriday: 465, eia: 1089 },
  { weekEnding: '2024-05-03', alphaBblFriday: 2803, eia: 1880 },
  { weekEnding: '2024-05-10', alphaBblFriday: -664, eia: -341 },
  { weekEnding: '2024-05-17', alphaBblFriday: 1448, eia: 1325 },
  { weekEnding: '2024-05-24', alphaBblFriday: -1801, eia: -1766 },
  { weekEnding: '2024-05-31', alphaBblFriday: 734, eia: 854 },
  { weekEnding: '2024-06-07', alphaBblFriday: -1527, eia: -1593 },
  { weekEnding: '2024-06-14', alphaBblFriday: 545, eia: 307 },
  { weekEnding: '2024-06-21', alphaBblFriday: -547, eia: -226 },
  { weekEnding: '2024-06-28', alphaBblFriday: 470, eia: 345 },
  { weekEnding: '2024-07-05', alphaBblFriday: -952, eia: -702 },
  { weekEnding: '2024-07-12', alphaBblFriday: -892, eia: -875 },
  { weekEnding: '2024-07-19', alphaBblFriday: -1249, eia: -1708 },
  { weekEnding: '2024-07-26', alphaBblFriday: -1459, eia: -1106 },
  { weekEnding: '2024-08-02', alphaBblFriday: 546, eia: 579 },
  { weekEnding: '2024-08-09', alphaBblFriday: -1959, eia: -1665 },
  { weekEnding: '2024-08-16', alphaBblFriday: -201, eia: -560 },
  { weekEnding: '2024-08-23', alphaBblFriday: -752, eia: -668 },
  { weekEnding: '2024-08-30', alphaBblFriday: -1536, eia: -1142 },
  { weekEnding: '2024-09-06', alphaBblFriday: -1558, eia: -1704 },
  { weekEnding: '2024-09-13', alphaBblFriday: -2237, eia: -1979 },
  { weekEnding: '2024-09-20', alphaBblFriday: -356, eia: 116 },
  { weekEnding: '2024-09-27', alphaBblFriday: 1144, eia: 840 },
  { weekEnding: '2024-10-04', alphaBblFriday: 1223, eia: 1247 },
  { weekEnding: '2024-10-11', alphaBblFriday: 657, eia: 108 },
  { weekEnding: '2024-10-18', alphaBblFriday: -1157, eia: -346 },
  { weekEnding: '2024-10-25', alphaBblFriday: 522, eia: 681 },
  { weekEnding: '2024-11-01', alphaBblFriday: 639, eia: 522 },
  { weekEnding: '2024-11-08', alphaBblFriday: -744, eia: -688 },
  { weekEnding: '2024-11-15', alphaBblFriday: 58, eia: -140 },
  { weekEnding: '2024-11-22', alphaBblFriday: -1154, eia: -909 },
  { weekEnding: '2024-11-29', alphaBblFriday: -146, eia: 50 },
  { weekEnding: '2024-12-06', alphaBblFriday: -1210, eia: -1298 },
  { weekEnding: '2024-12-13', alphaBblFriday: 495, eia: 108 },
  { weekEnding: '2024-12-20', alphaBblFriday: -342, eia: -320 },
  { weekEnding: '2024-12-27', alphaBblFriday: -508, eia: -142 },
  { weekEnding: '2025-01-03', alphaBblFriday: -2448, eia: -2502 },
  { weekEnding: '2025-01-10', alphaBblFriday: -38, eia: 765 },
  { weekEnding: '2025-01-17', alphaBblFriday: 762, eia: -148 },
  { weekEnding: '2025-01-24', alphaBblFriday: -45, eia: 326 },
  { weekEnding: '2025-01-31', alphaBblFriday: 35, eia: -34 },
  { weekEnding: '2025-02-07', alphaBblFriday: 1460, eia: 872 },
  { weekEnding: '2025-02-14', alphaBblFriday: 1570, eia: 1472 },
  { weekEnding: '2025-02-21', alphaBblFriday: 1151, eia: 1282 },
  { weekEnding: '2025-02-28', alphaBblFriday: 894, eia: 1124 },
  { weekEnding: '2025-03-07', alphaBblFriday: -1114, eia: -1228 },
  { weekEnding: '2025-03-14', alphaBblFriday: -531, eia: -1009 },
  { weekEnding: '2025-03-21', alphaBblFriday: -1448, eia: -755 },
  { weekEnding: '2025-03-28', alphaBblFriday: 3061, eia: 2373 },
  { weekEnding: '2025-04-04', alphaBblFriday: 809, eia: 681 },
  { weekEnding: '2025-04-11', alphaBblFriday: -674, eia: -654 },
  { weekEnding: '2025-04-18', alphaBblFriday: 546, eia: -86 },
  { weekEnding: '2025-04-25', alphaBblFriday: -186, eia: 682 },
  { weekEnding: '2025-05-02', alphaBblFriday: -347, eia: -740 },
  { weekEnding: '2025-05-09', alphaBblFriday: -1237, eia: -1069 },
  { weekEnding: '2025-05-16', alphaBblFriday: -96, eia: -457 },
  { weekEnding: '2025-05-23', alphaBblFriday: 346, eia: 75 },
  { weekEnding: '2025-05-30', alphaBblFriday: 547, eia: 576 },
  { weekEnding: '2025-06-06', alphaBblFriday: -660, eia: -403 },
  { weekEnding: '2025-06-13', alphaBblFriday: -1007, eia: -995 },
  { weekEnding: '2025-06-20', alphaBblFriday: -256, eia: -464 },
  { weekEnding: '2025-06-27', alphaBblFriday: -1973, eia: -1493 },
  { weekEnding: '2025-07-04', alphaBblFriday: 718, eia: 464 },
  { weekEnding: '2025-07-11', alphaBblFriday: -41, eia: 213 },
  { weekEnding: '2025-07-18', alphaBblFriday: 243, eia: 455 },
  { weekEnding: '2025-07-25', alphaBblFriday: 825, eia: 690 },
  { weekEnding: '2025-08-01', alphaBblFriday: 551, eia: 453 },
  { weekEnding: '2025-08-08', alphaBblFriday: -1026, eia: 45 },
  { weekEnding: '2025-08-15', alphaBblFriday: 1141, eia: 419 },
  { weekEnding: '2025-08-22', alphaBblFriday: -417, eia: -838 },
  { weekEnding: '2025-08-29', alphaBblFriday: 415, eia: 1590 },
  { weekEnding: '2025-09-05', alphaBblFriday: 696, eia: -365 },
  { weekEnding: '2025-09-12', alphaBblFriday: -256, eia: -296 },
  { weekEnding: '2025-09-19', alphaBblFriday: 46, eia: 177 },
  { weekEnding: '2025-09-26', alphaBblFriday: -490, eia: -271 },
  { weekEnding: '2025-10-03', alphaBblFriday: -609, eia: -763 },
  { weekEnding: '2025-10-10', alphaBblFriday: -1100, eia: -703 },
  { weekEnding: '2025-10-17', alphaBblFriday: -547, eia: -770 },
  { weekEnding: '2025-10-24', alphaBblFriday: 1102, eia: 1334 },
  { weekEnding: '2025-10-31', alphaBblFriday: 985, eia: 300 },
  { weekEnding: '2025-11-07', alphaBblFriday: -553, eia: -346 },
  { weekEnding: '2025-11-14', alphaBblFriday: -431, eia: -698 },
  { weekEnding: '2025-11-21', alphaBblFriday: -217, eia: -68 },
  { weekEnding: '2025-11-28', alphaBblFriday: -173, eia: -457 },
  { weekEnding: '2025-12-05', alphaBblFriday: 80, eia: 308 },
  { weekEnding: '2025-12-12', alphaBblFriday: -551, eia: -742 },
  { weekEnding: '2025-12-19', alphaBblFriday: 1040, eia: 707 },
  { weekEnding: '2025-12-26', alphaBblFriday: 443, eia: 543 },
  { weekEnding: '2026-01-02', alphaBblFriday: 422, eia: 728 },
  { weekEnding: '2026-01-09', alphaBblFriday: 534, eia: 745 },
  { weekEnding: '2026-01-16', alphaBblFriday: 966, eia: 1478 },
  { weekEnding: '2026-01-23', alphaBblFriday: 298, eia: -278 },
  { weekEnding: '2026-01-30', alphaBblFriday: -1097, eia: -743 },
  { weekEnding: '2026-02-06', alphaBblFriday: 637, eia: 1071 },
  { weekEnding: '2026-02-13', alphaBblFriday: -410, eia: -1095 },
  { weekEnding: '2026-02-20', alphaBblFriday: 530, eia: null }
];

function formatWeekLabel(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: '2-digit'
  });
}

function formatNumber(value) {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'N/A';
  }

  return value.toLocaleString();
}

function parseWeekEnding(value) {
  return new Date(`${value}T00:00:00`);
}

function subtractOneYear(date) {
  const result = new Date(date);
  result.setFullYear(result.getFullYear() - 1);
  return result;
}

function getChartDateRange(data) {
  if (!data.length) {
    return {
      startDate: null,
      endDate: null
    };
  }

  return {
    startDate: parseWeekEnding(data[0].weekEnding),
    endDate: parseWeekEnding(data[data.length - 1].weekEnding)
  };
}

function getDefaultDateRange(data) {
  const fullRange = getChartDateRange(data);

  if (!fullRange.endDate) {
    return fullRange;
  }

  const oneYearStart = subtractOneYear(fullRange.endDate);
  return {
    startDate: oneYearStart > fullRange.startDate ? oneYearStart : fullRange.startDate,
    endDate: fullRange.endDate
  };
}

function normalizeAlphaValue(value) {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const numberValue = Number(value);
  return Number.isNaN(numberValue) ? null : numberValue;
}

function normalizeAlphaRow(row) {
  const weekEnding = row.weekEnding || row.collection_date || row.report_date;
  return {
    weekEnding,
    alphaBblFriday: normalizeAlphaValue(row.alphaBblFriday ?? row.change_value),
    eia: normalizeAlphaValue(row.eia),
    eiaInventory: normalizeAlphaValue(row.eiaInventory),
    series: row.series,
    seriesDescription: row.seriesDescription,
    units: row.units,
  };
}

function mergeAlphaBblData(baseData, alphaRows) {
  const dataByWeek = baseData.reduce((acc, entry) => {
    acc[entry.weekEnding] = { ...entry };
    return acc;
  }, {});

  alphaRows.map(normalizeAlphaRow).forEach((entry) => {
    if (!entry.weekEnding) {
      return;
    }

    const existingEntry = dataByWeek[entry.weekEnding] || { eia: null };
    dataByWeek[entry.weekEnding] = {
      ...existingEntry,
      weekEnding: entry.weekEnding,
      alphaBblFriday: entry.alphaBblFriday ?? existingEntry.alphaBblFriday,
      eia: entry.eia ?? existingEntry.eia,
      eiaInventory: entry.eiaInventory ?? existingEntry.eiaInventory,
      series: entry.series ?? existingEntry.series,
      seriesDescription: entry.seriesDescription ?? existingEntry.seriesDescription,
      units: entry.units ?? existingEntry.units,
    };
  });

  return Object.values(dataByWeek).sort((a, b) => (
    parseWeekEnding(a.weekEnding) - parseWeekEnding(b.weekEnding)
  ));
}

function buildChartData(data, startingInventory) {
  const latestAlphaIndex = data.reduce((latestIndex, entry, index) => (
    entry.alphaBblFriday !== null && entry.alphaBblFriday !== undefined
      ? index
      : latestIndex
  ), -1);
  const latestAlphaEntry = latestAlphaIndex >= 0 ? data[latestAlphaIndex] : null;
  const latestAlphaOnlyIndex = latestAlphaEntry && (latestAlphaEntry.eia === null || latestAlphaEntry.eia === undefined)
    ? latestAlphaIndex
    : -1;

  let alphaRunning = startingInventory;
  let eiaRunning = startingInventory;

  return data.map((entry, index) => {
    const nextEntry = {
      ...entry,
      isLatestAlphaOnly: index === latestAlphaOnlyIndex
    };

    if (startingInventory !== null && startingInventory !== undefined) {
      alphaRunning += entry.alphaBblFriday || 0;
      eiaRunning += entry.eia || 0;
      nextEntry.alphaCumulative = alphaRunning;
      nextEntry.eiaCumulative = eiaRunning;
    }

    return nextEntry;
  });
}

const CustomTooltip = ({ active, payload, label, view }) => {
  if (!active || !payload || !payload.length) {
    return null;
  }

  return (
    <Paper style={{ padding: 8, border: '1px solid #ccc' }}>
      <Typography variant="body2">{formatWeekLabel(label)}</Typography>
      {payload.map((entry) => (
        <Typography
          key={entry.dataKey}
          variant="body2"
          style={{ color: entry.color }}
        >
          {entry.name}: {formatNumber(entry.value)} {view === 'change' ? 'BBL' : 'total BBL'}
        </Typography>
      ))}
      {payload.some((entry) => entry.payload && entry.payload.isLatestAlphaOnly) && (
        <Typography variant="body2" style={{ color: ALPHA_LATEST_COLOR }}>
          Latest AlphaBBL estimate. EIA pending.
        </Typography>
      )}
    </Paper>
  );
};

export default function InventoryChangeChart({
  data = defaultInventoryChangeData,
  title = 'Cushing Inventory',
  view = 'change',
  startingInventory
}) {
  const classes = useStyles();
  const theme = useTheme();
  const { auth, alphabbl } = useAuth();
  const { setMessage } = useMessage();
  const [chartSourceData, setChartSourceData] = useState(data);
  const fullDateRange = useMemo(() => getChartDateRange(chartSourceData), [chartSourceData]);
  const initialDateRange = useMemo(() => getDefaultDateRange(chartSourceData), [chartSourceData]);
  const [dateRange, setDateRange] = useState(initialDateRange);
  const [seriesVisibility, setSeriesVisibility] = useState({
    alphaBblFriday: true,
    eia: true
  });

  useEffect(() => {
    if (Number(alphabbl) !== 1) {
      return;
    }

    fetch('/api/alphabbl/cushing/inventory-change', {
      method: 'GET',
      headers: {
        'x-access-token': sessionStorage.getItem('token')
      }
    })
    .then(response => auth(response))
    .then((response) => {
      if (response.data) {
        setChartSourceData(mergeAlphaBblData(data, response.data));
      } else if (response.message) {
        setMessage(response.message);
      }
    })
    .catch((error) => {
      console.error('Error:', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [alphabbl]);

  useEffect(() => {
    setDateRange(initialDateRange);
  }, [initialDateRange]);

  const filteredSourceData = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) {
      return chartSourceData;
    }

    const start = new Date(dateRange.startDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(dateRange.endDate);
    end.setHours(23, 59, 59, 999);

    return chartSourceData.filter((entry) => {
      const entryDate = parseWeekEnding(entry.weekEnding);
      return entryDate >= start && entryDate <= end;
    });
  }, [chartSourceData, dateRange]);
  const chartData = buildChartData(filteredSourceData, startingInventory);
  const isCumulative = view === 'cumulative' && startingInventory !== null && startingInventory !== undefined;
  const handleSeriesToggle = (event) => {
    const { name, checked } = event.target;
    setSeriesVisibility((current) => ({
      ...current,
      [name]: checked
    }));
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="button" className={classes.title}>
        {title}
      </Typography>
      <Grid container justifyContent='center'>
        <FormControlLabel
          className={classes.filter}
          control={
            <DateRangeFilter
              value={dateRange}
              onChange={setDateRange}
              definedRanges={[
                {
                  label: 'Full Range',
                  startDate: fullDateRange.startDate,
                  endDate: fullDateRange.endDate
                },
                {
                  label: 'Last Year',
                  startDate: initialDateRange.startDate,
                  endDate: initialDateRange.endDate
                },
                {
                  label: 'YTD',
                  startDate: new Date(new Date().getFullYear(), 0, 1),
                  endDate: new Date()
                },
                {
                  label: 'Last 90 Days',
                  startDate: new Date(new Date().setDate(new Date().getDate() - 90)),
                  endDate: new Date()
                },
                {
                  label: 'Last 180 Days',
                  startDate: new Date(new Date().setDate(new Date().getDate() - 180)),
                  endDate: new Date()
                },
                {
                  label: 'Reset',
                  startDate: initialDateRange.startDate,
                  endDate: initialDateRange.endDate
                }
              ]}
            />
          }
        />
      </Grid>
      <Grid container justifyContent='center'>
        <FormGroup row className={classes.seriesFilters}>
          <FormControlLabel
            control={
              <Checkbox
                checked={seriesVisibility.alphaBblFriday}
                onChange={handleSeriesToggle}
                name='alphaBblFriday'
                style={{ color: theme.palette.primary.main }}
              />
            }
            label='AlphaBBL'
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={seriesVisibility.eia}
                onChange={handleSeriesToggle}
                name='eia'
                style={{ color: '#03a86b' }}
              />
            }
            label='EIA'
          />
        </FormGroup>
      </Grid>
      <div className={classes.chartArea}>
      <ResponsiveContainer width="100%" height="100%">
        {isCumulative ? (
          <ComposedChart
            data={chartData}
            margin={{
              top: 16,
              right: 24,
              bottom: 24,
              left: 16
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="weekEnding"
              minTickGap={24}
              tickFormatter={formatWeekLabel}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip view="cumulative" />} />
            <Legend />
            {seriesVisibility.alphaBblFriday && (
              <Line
                type="monotone"
                dataKey="alphaCumulative"
                name="AlphaBBL Friday"
                stroke={ALPHA_COLOR}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            )}
            {seriesVisibility.eia && (
              <Line
                type="monotone"
                dataKey="eiaCumulative"
                name="EIA"
                stroke={EIA_COLOR}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            )}
          </ComposedChart>
        ) : (
          <BarChart
            data={chartData}
            margin={{
              top: 16,
              right: 24,
              bottom: 24,
              left: 16
            }}
            barCategoryGap="8%"
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="weekEnding"
              minTickGap={24}
              tickFormatter={formatWeekLabel}
              stroke={theme.palette.text.secondary}
            />
            <YAxis stroke={theme.palette.text.secondary} />
            <Tooltip content={<CustomTooltip view="change" />} />
            <Legend />
            <ReferenceLine y={0} stroke={theme.palette.text.secondary} />
            {seriesVisibility.alphaBblFriday && (
              <Bar
                dataKey="alphaBblFriday"
                name="AlphaBBL Friday"
                fill={ALPHA_COLOR}
                maxBarSize={18}
              >
                {chartData.map((entry) => (
                  <Cell
                    key={`alpha-cell-${entry.weekEnding}`}
                    fill={entry.isLatestAlphaOnly ? ALPHA_LATEST_COLOR : ALPHA_COLOR}
                  />
                ))}
              </Bar>
            )}
            {seriesVisibility.eia && (
              <Bar
                dataKey="eia"
                name="EIA"
                fill={EIA_COLOR}
                maxBarSize={18}
              />
            )}
          </BarChart>
        )}
      </ResponsiveContainer>
      </div>
      {chartData.some((entry) => entry.isLatestAlphaOnly) && (
        <Typography variant="body2" style={{ marginTop: 8, color: ALPHA_LATEST_COLOR }}>
          Highlighted bar marks the latest AlphaBBL estimate before the corresponding EIA release.
        </Typography>
      )}
    </Paper>
  );
}
